import { AgreementTerms } from "./AgreementJourney";
import React from "react";
import { Link } from "react-router-dom";
import ThemedSelect from "components/FormControls/ThemedSelect";
import DrawnSignature from "components/DrawnSignature";
import { formatAbn } from "utils/abn";
import "./agreementEditor.css";
import "./agreementReview.css";

export default function AgreementEditor({
  data,
  selected,
  contactID,
  onContact,
  scope,
  onScope,
  buyerRepresentative,
  onBuyerRepresentative,
  supplierRepresentative,
  onSupplierRepresentative,
  signature,
  onSignature,
  gstConsent,
  onGstConsent,
  consent,
  onConsent,
  busy,
  onManageContact,
  onVerify,
  onSave,
  onReview,
}) {
  const dark = document.documentElement.dataset.theme === "dark";
  const blocked =
    busy ||
    !data.canWrite ||
    !selected ||
    selected.reasons.length > 0 ||
    !scope.trim() ||
    !buyerRepresentative.name.trim() ||
    !buyerRepresentative.position.trim() ||
    !supplierRepresentative.name.trim() ||
    !supplierRepresentative.position.trim() ||
    !signature ||
    !consent ||
    !gstConsent;
  const update = (current, setter, field) => (event) =>
    setter({ ...current, [field]: event.target.value });
  const required = (
    <span className="agreement-editor-required" aria-hidden="true">
      {" "}
      *
    </span>
  );
  return (
    <>
      <div className="agreement-editor-status">
        <span className="rcti-agreement-badge rcti-agreement-badge--proposed">
          Proposed · Not sent
        </span>
        <span>Local draft · Wording v{data.version}</span>
      </div>
      <div className="agreement-editor-columns">
        <div className="agreement-editor-stack">
          <section className="rcti-agreement-card">
            <h2>Parties</h2>
            <div className="agreement-editor-fields">
              <label>
                <span>Buyer / issuer legal name{required}</span>
                <input readOnly value={data.buyer.name} />
              </label>
              <label>
                <span>Supplier / vendor legal name{required}</span>
                <ThemedSelect
                  dark={dark}
                  aria-label="Supplier / vendor legal name"
                  value={contactID}
                  onChange={(event) => onContact(event.target.value)}
                >
                  <option value="">Select a supplier</option>
                  <option value="add-new-contact">Add new contact</option>
                  {data.contacts.map((contact) => (
                    <option value={contact._id} key={contact._id}
                      data-status={contact.reasons?.length ? "Not eligible" : "Eligible"}>
                      {contact.companyName || contact.name}
                    </option>
                  ))}
                </ThemedSelect>
              </label>
              <label>
                <span>Buyer ABN{required}</span>
                <input readOnly value={formatAbn(data.buyer.abnNumber)} />
              </label>
              <label>
                <span>Supplier ABN{required}</span>
                <input readOnly value={formatAbn(selected?.abnNumber)} />
              </label>
            </div>
            {!data.contacts.length && (
              <p role="status">
                No suppliers are available for a new agreement. Suppliers with
                active agreements are excluded; view their agreements in the
                register.
              </p>
            )}
            <p>
              Legal identities and eligibility declarations come from your
              business profile and supplier contact.
            </p>
            <div className="agreement-editor-links">
              <Link to="/MyProfile?returnTo=%2Frcti-agreements%2Fnew">
                Business profile
              </Link>
              {selected && (
                <Link
                  to={`/contacts/editContact/${selected._id}?returnTo=%2Frcti-agreements%2Fnew`}
                  onClick={(event) => {
                    if (onManageContact) {
                      event.preventDefault();
                      onManageContact(selected._id);
                    }
                  }}
                >
                  Edit supplier contact
                </Link>
              )}
              <button
                type="button"
                className="rcti-button-secondary"
                onClick={onVerify}
              >
                Review verification
              </button>
            </div>
            {selected?.reasons.length > 0 && (
              <ul className="rcti-agreements__error" role="alert">
                {selected.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </section>
          <section className="rcti-agreement-card">
            <h2>Scope and duration</h2>
            <label>
              <span>Supplies covered{required}</span>
              <textarea
                required
                rows={3}
                maxLength={2000}
                value={scope}
                onChange={(event) => onScope(event.target.value)}
                placeholder="Describe the covered taxable goods or services. The buyer determines their value."
              />
            </label>
            <div className="agreement-editor-fields">
              <label>
                Effective date
                <ThemedSelect
                  dark={dark}
                  aria-label="Effective date"
                  value="acceptance"
                >
                  <option value="acceptance">Upon supplier acceptance</option>
                </ThemedSelect>
              </label>
              <label>
                Agreed expiry
                <ThemedSelect
                  dark={dark}
                  aria-label="Agreed expiry"
                  value="ongoing"
                >
                  <option value="ongoing">No fixed expiry</option>
                </ThemedSelect>
              </label>
            </div>
            <p>
              No agreement is active before supplier acceptance. Once signed, it
              continues while both parties remain eligible, unless either party
              suspends it.
            </p>
            <label>
              Wording version
              <ThemedSelect
                dark={dark}
                aria-label="Wording version"
                value={data.version}
              >
                <option value={data.version}>
                  v{data.version} · Current agreement wording
                </option>
              </ThemedSelect>
            </label>
          </section>
          <section className="rcti-agreement-card agreement-editor-terms">
            <h2>Agreement terms</h2>
            <p>
              These are the exact terms included in the document sent for
              supplier signature.
            </p>
            <AgreementTerms terms={data.terms} structured />
          </section>
          <section className="rcti-agreement-card">
            <h2>Buyer acknowledgement and authority</h2>
            <label className="rcti-consent">
              <input
                type="checkbox"
                checked={gstConsent}
                onChange={(event) => onGstConsent(event.target.checked)}
              />
              I acknowledge that {data.buyer.name} is registered for GST and
              will notify the supplier if it ceases to be registered.
            </label>
            <label className="rcti-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => onConsent(event.target.checked)}
              />
              I am authorised to propose this agreement for {data.buyer.name},
              confirm these eligibility settings and adopt my drawing as my
              electronic signature.
            </label>
            <div className="agreement-editor-fields">
              <label>
                <span>Buyer authorised representative{required}</span>
                <input
                  required
                  maxLength={160}
                  value={buyerRepresentative.name}
                  onChange={update(
                    buyerRepresentative,
                    onBuyerRepresentative,
                    "name"
                  )}
                />
              </label>
              <label>
                <span>Position / authority{required}</span>
                <input
                  required
                  maxLength={160}
                  value={buyerRepresentative.position}
                  onChange={update(
                    buyerRepresentative,
                    onBuyerRepresentative,
                    "position"
                  )}
                />
              </label>
            </div>
            <label>
              Buyer email
              <input
                readOnly
                value={data.buyer.emailAddress || ""}
                placeholder="Business email not recorded"
              />
            </label>
            <DrawnSignature
              value={signature}
              onChange={onSignature}
              disabled={busy}
            />
            <p>
              Your drawing is recorded when the agreement is sent. Supplier
              acceptance and signature are still required.
            </p>
          </section>
        </div>
        <aside className="agreement-editor-stack">
          <section className="rcti-agreement-card">
            <h2>Supplier recipient</h2>
            <label>
              <span>Supplier signatory / recipient{required}</span>
              <input
                required
                maxLength={160}
                value={supplierRepresentative.name}
                onChange={update(
                  supplierRepresentative,
                  onSupplierRepresentative,
                  "name"
                )}
                placeholder="Full name"
              />
            </label>
            <label>
              <span>Position{required}</span>
              <input
                required
                maxLength={160}
                value={supplierRepresentative.position}
                onChange={update(
                  supplierRepresentative,
                  onSupplierRepresentative,
                  "position"
                )}
                placeholder="Position / authority"
              />
            </label>
            <label>
              <span>Invitation email{required}</span>
              <input
                readOnly
                value={selected?.emailAddress || ""}
                placeholder="Select a supplier"
              />
            </label>
            <p>
              The recipient must have authority to accept for{" "}
              {selected?.companyName || selected?.name || "the supplier"}. Their
              authority is confirmed during signing. Update the contact to
              change this email.
            </p>
          </section>
          <section className="rcti-agreement-card">
            <h2>What happens next?</h2>
            <strong>Review, then invite</strong>
            <p>
              Review the agreement document and invitation email before sending.
            </p>
            <hr />
            <p>
              Sending creates an awaiting-signature agreement. Supplier
              acceptance makes it active, subject to eligibility.
            </p>
            <p>No RCTI number, invoice or supplier payable is generated.</p>
          </section>
          <div className="agreement-editor-note">
            <strong>Seven days to sign</strong>
            <p>
              The signing window starts when you send the agreement. Saving this
              tab's draft does not send an invitation or start that window.
            </p>
          </div>
        </aside>
      </div>
      <footer className="agreement-editor-footer">
        <button
          type="button"
          className="rcti-button-secondary"
          onClick={onVerify}
          disabled={busy}
        >
          ← Back to verification
        </button>
        <div>
          <button
            type="button"
            className="rcti-button-secondary"
            onClick={onSave}
            disabled={busy}
          >
            Save &amp; leave to agreements
          </button>
          <button type="button" onClick={onReview} disabled={blocked}>
            Review agreement
          </button>
        </div>
      </footer>
    </>
  );
}
